import moment from 'moment';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@common/ui/buttons/Button';
import { CachedImage } from '@common/ui/CachedImage';

import { type PostMatchmakerRecommendationResponse } from '@services/MatchmakerService';

import styles from './RecommendResultSheet.module.scss';
import { shareRecommendation } from '../../utils/shareRecommendation';
import { SheetOverlay } from '../SheetOverlay';

export type RecommendResultSheetProps = {
  recommendation: PostMatchmakerRecommendationResponse;
  onDone: () => void;
};

/** The share's button, as the bot sends it (server share.service.js). */
const SHARE_BUTTON_TEXT = 'Открыть Kiss';

const botNameOf = (link: string) =>
  /^https:\/\/(?:t|telegram)\.me\/([^/?#]+)/.exec(link)?.[1] ?? null;

const splitShareText = (shareText: string) => {
  const [title, ...rest] = shareText.split('\n\n');

  return { title, body: rest.join('\n\n') };
};

/**
 * «Рекомендация готова» (board 3.4, review S7): the invite already exists, so
 * the share runs once on its own and can be repeated; «Готово» closes.
 */
export const RecommendResultSheet = ({
  recommendation,
  onDone,
}: RecommendResultSheetProps) => {
  const [isPreparedAllowed, setIsPreparedAllowed] = useState(true);
  const isAutoSharedRef = useRef(false);
  const [sentAt] = useState(() => moment().format('HH:mm'));

  const share = async (isPreparedAllowedNow: boolean) => {
    const { isPreparedRefused } = await shareRecommendation(recommendation, {
      isPreparedAllowed: isPreparedAllowedNow,
    });

    if (isPreparedRefused) setIsPreparedAllowed(false);
  };

  useEffect(() => {
    if (isAutoSharedRef.current) return;

    isAutoSharedRef.current = true;
    void share(true);
  }, []);

  const botName = botNameOf(recommendation.link);
  const { title, body } = splitShareText(recommendation.share_text);

  return (
    <SheetOverlay
      testId="recommend-result-sheet"
      label="Рекомендация готова"
      onDismiss={onDone}
    >
      <div className={styles.tile}>
        <CachedImage
          testId="recommend-result-sheet-icon"
          src="/icons/3d-icons/message.png"
          alt=""
          className={styles.tileImage}
        />
      </div>

      <header className={styles.header}>
        <h2 className={styles.title}>Рекомендация готова</h2>
        <p className={styles.subtitle}>
          Если сообщение не ушло — отправьте его ещё раз. Ссылка работает 7
          дней.
        </p>
      </header>

      <div className={styles.body}>
        <p className={styles.caption}>Так друг увидит сообщение</p>
        <div
          className={styles.chat}
          data-test-id="recommend-result-sheet-preview"
        >
          <div className={styles.message}>
            <div className={styles.bubble}>
              {botName && <div className={styles.via}>через @{botName}</div>}
              <div className={styles.messageTitle}>{title}</div>
              {body && (
                <div
                  className={`${styles.messageBody} ym-hide-content`}
                  data-test-id="recommend-result-sheet-preview-note"
                >
                  {body}
                </div>
              )}
              <div className={styles.time}>{sentAt}</div>
            </div>
            <div className={styles.messageButton}>{SHARE_BUTTON_TEXT}</div>
          </div>
        </div>
        <p className={styles.footnote}>Без имени и фото анкеты</p>
      </div>

      <div className={styles.buttons}>
        <Button
          testId="recommend-result-sheet-share-button"
          variant="actionSecondary"
          onClick={() => void share(isPreparedAllowed)}
        >
          Поделиться ещё раз
        </Button>
        <Button
          testId="recommend-result-sheet-done-button"
          variant="actionPrimary"
          onClick={onDone}
        >
          Готово
        </Button>
      </div>
    </SheetOverlay>
  );
};
